import { TradeoffTypeEnum } from "@clockwise/schema";
import { Portal, Transition } from "@headlessui/react";
import classNames from "classnames";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { usePopper } from "react-popper";
import { SchedulingOptionsTradeoffs } from "../chat/ai-chat/utils/types";
import {
  AvailabilityIssue,
  FixableConflict,
  NoIssues,
  UnfixableConflict,
  WorksForEveryone,
} from "./Tradeoffs";
const POPOVER_WIDTH = 250;

export const TradeoffsPopover = ({
  tradeoffs,
  side,
  totalAttendeeCount,
  anchorEl,
}: {
  tradeoffs: SchedulingOptionsTradeoffs;
  side: "left" | "right";
  totalAttendeeCount: number;
  anchorEl: React.RefObject<HTMLDivElement>;
}) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(anchorEl.current, container, {
    placement: side,
    strategy: "fixed",
    modifiers: [
      {
        name: "flip",
        enabled: true,
      },
      { name: "offset", options: { offset: [0, 2] } },
    ],
  });

  //Only show avaiblity issues and fixable conflicts
  const filteredTradeoffs: SchedulingOptionsTradeoffs = tradeoffs.filter(
    (tradeoff) =>
      tradeoff.tradeoffType === TradeoffTypeEnum.AVAILABILITY_ISSUE ||
      tradeoff.tradeoffType === TradeoffTypeEnum.FIXABLE_CONFLICT,
  );

  const uniqueTradeoffAttendees = new Set(
    [...filteredTradeoffs].flatMap((tradeoff) =>
      tradeoff.aggregateAttendees.map((attendee) => attendee.primaryCalendarId),
    ),
  );

  const showWorksForEveryone = isEmpty(filteredTradeoffs);
  const showRemaningNoIssues =
    !showWorksForEveryone && totalAttendeeCount > uniqueTradeoffAttendees.size;
  const width = showWorksForEveryone ? 160 : POPOVER_WIDTH;

  return (
    <Portal>
      <Transition
        show={true}
        leave="cw-transition cw-ease-in cw-duration-100"
        leaveFrom="cw-opacity-100"
        leaveTo="cw-opacity-0"
        enter="cw-transition cw-ease-in cw-duration-75"
        enterFrom="cw-opacity-0"
        enterTo="cw-opacity-100"
      >
        <div
          role="combobox"
          ref={setContainer}
          style={{
            ...styles.popper,
            width,
            zIndex: 10000,
          }}
          {...attributes.popper}
          className={classNames(
            "cw-px-3 cw-py-0.5 cw-shadow-md cw-rounded-lg cw-absolute cw-body-sm cw-bg-emphasis cw-hidden lg:cw-flex",
          )}
        >
          <div className="cw-flex-col cw-w-full">
            {showWorksForEveryone && <WorksForEveryone />}
            {filteredTradeoffs.map((tradeoff, index) => {
              const showBottomBorder = showRemaningNoIssues || index < filteredTradeoffs.length - 1;
              switch (tradeoff.tradeoffType) {
                case TradeoffTypeEnum.AVAILABILITY_ISSUE:
                  // The designs had unfixable conflicts as separate categories, so this logic finds the "Unfixable conflicts" and makes them their own category
                  if (tradeoff.title.includes("Unfixable conflict")) {
                    return (
                      <UnfixableConflict
                        tradeoff={tradeoff}
                        showBottomBorder={showBottomBorder}
                        key={tradeoff.id}
                      />
                    );
                  } else {
                    return (
                      <AvailabilityIssue
                        tradeoff={tradeoff}
                        showBottomBorder={showBottomBorder}
                        key={tradeoff.id}
                      />
                    );
                  }
                case TradeoffTypeEnum.FIXABLE_CONFLICT:
                  return (
                    <FixableConflict
                      tradeoff={tradeoff}
                      showBottomBorder={showBottomBorder}
                      key={tradeoff.id}
                    />
                  );
                default:
                  return null;
              }
            })}
            {showRemaningNoIssues && (
              <NoIssues
                numerator={totalAttendeeCount - uniqueTradeoffAttendees.size}
                denominator={totalAttendeeCount}
              />
            )}
          </div>
        </div>
      </Transition>
    </Portal>
  );
};
